import styled from "@emotion/styled";
import React, { FC, useEffect } from "react";
import { PressEvent } from "react-aria";
import { Button as AriaButton, ButtonProps } from "react-aria-components";
import GlassFrame from "../frame/GlassFrame";

const ButtonStyled = styled(AriaButton)`
  --btn-height: 3.0625rem;
  --border-radius: 7.5rem;
  --btn-font-weight: inherit;
  display: block;
  margin: 0;
  height: var(--btn-height);
  border-radius: var(--border-radius);
  background: var(--btn-background, #fff);
  color: var(--btn-color, var(--greys-charcoal, #212121));
  border: 0;
  padding: 0;
  outline: 0;
  cursor: pointer;
  transition:
    box-shadow 0.2s ease,
    opacity 0.2s ease,
    color 0.2s ease,
    filter 0.2s ease;
  font-family: inherit;
  font-size: inherit;
  font-style: inherit;
  font-weight: inherit;
  min-width: max-content;
  line-height: inherit;
  --btn-padding-left: 1.75rem;
  --btn-padding-right: 1rem;
  --btn-padding-top: 1rem;
  --btn-padding-bottom: 0.9375rem;

  --border-size: 0px;
  --btn-padding: var(--btn-padding-top) var(--btn-padding-right)
    var(--btn-padding-bottom) var(--btn-padding-left);
  --btn-gap: 0.375rem;

  &:focus {
    outline: revert;
  }

  &[data-grow="true"] {
    width: 100%;
  }

  &[data-center="true"] {
    margin: 0 auto;
  }

  &[data-hide-arrow="true"] {
    --btn-padding-right: 1.75rem;
    --btn-gap: 0;
  }

  // #region Themes
  &[data-theme="card"] {
    --btn-height: 4.5rem;
    --border-radius: 0.7rem;
    box-shadow:
      0px 0px 0px 0px rgba(0, 0, 0, 0.1),
      -13px 11px 17px 0px rgba(0, 0, 0, 0.1),
      -28px 24px 22px 0px rgba(0, 0, 0, 0.1),
      -50px 43px 26px 0px rgba(0, 0, 0, 0.03),
      -79px 67px 29px 0px rgba(0, 0, 0, 0);
  }

  &[data-theme="danger"] {
    --local-color-danger: #bf1902;
    --inner-background: var(--local-color-danger);
    --btn-background: var(--local-color-danger);
    --btn-color: #fff;
    --inner-opacity: 1;
    --focus-btn-color: #fff;

    &[data-outline="false"] {
      --focus-inner-opacity: 0;
    }

    &[data-outline="true"] {
      --inner-background: #fff;
      --btn-color: var(--local-color-danger);
      --btn-background: var(--local-color-danger);
      --inner-opacity: 1;
      --border-size: 2px;
    }
  }

  &[data-theme="sunrise"] {
    --btn-background: var(
      --sunrise-horizontal-regular,
      linear-gradient(90deg, #80aeff 0.36%, #f7bde6 52.67%, #ffbd70 99.65%)
    );
    --inner-background: #fff;
    --inner-opacity: 0.3;

    &[data-outline="false"] {
      --focus-inner-opacity: 0;
    }

    &[data-outline="initial"] {
      --inner-opacity: 1;
      --focus-inner-opacity: 0.5;
      --border-size: 1px;
    }
  }

  &[data-theme="charcoal"] {
    --inner-background: var(--greys-charcoal, #212121);
    --btn-background: var(
      --sunrise-horizontal-dusk,
      linear-gradient(90deg, #0e3763 0%, #5b4855 52.6%, #8a6947 100%)
    );
    --btn-color: #fff;
    --inner-opacity: 1;

    &[data-outline="false"] {
      --focus-inner-opacity: 0;
    }
  }

  &[data-theme="dusk"] {
    --inner-background: var(--greys-charcoal, #212121);
    --btn-background: var(
      --sunrise-horizontal-dusk,
      linear-gradient(90deg, #0e3763 0%, #5b4855 52.6%, #8a6947 100%)
    );
    --btn-color: #fff;
    --focus-btn-color: #fff;
    --inner-opacity: 0;

    &[data-outline="false"] {
      --focus-inner-opacity: 0;
    }
  }

  &[data-theme="blur-charcoal"] {
    --btn-background: var(--white-26, rgba(255, 255, 255, 0.26));
    --inner-background: var(
      --sunrise-horizontal-dusk,
      linear-gradient(90deg, #0e3763 0%, #5b4855 52.6%, #8a6947 100%)
    );
    --btn-color: var(--greys-charcoal, #212121);
    --inner-opacity: 0;
    --focus-inner-opacity: 1;
    --focus-btn-color: #fff;

    &[data-active="true"] {
      --inner-opacity: 1;
      --btn-color: #fff;
    }
  }

  &[data-theme="transparent"] {
    --inner-background: transparent;
    --btn-background: transparent;

    &[data-outline="true"] {
      --inner-background: transparent;
      --inner-opacity: 1;
      --border-size: 1px;
      border: 1px solid black;
    }
  }

  &[data-theme="rxdiet"] {
    --btn-background: var(--Partners-RxDiet-Primary, #6235cc);
    --btn-color: #fff;
    --focus-btn-color: #fff;
    --inner-background: #fff;
    --inner-opacity: 0;
    --focus-inner-opacity: 0.2;
  }

  &[data-theme="glass"] {
    --btn-background: transparent;
    --btn-color: var(--greys-charcoal, #212121);
    --focus-btn-color: var(--greys-charcoal, #212121);
    --inner-background: transparent;
    --inner-opacity: 0;
    --frame-corner-shape: round;
  }

  &[data-theme="simple"] {
    --btn-background: transparent;
    --btn-color: var(--greys-charcoal, #212121);
    --focus-btn-color: var(--greys-charcoal, #212121);
    --inner-background: transparent;
    --inner-opacity: 0;
  }

  &[data-theme="subtle"] {
    --btn-background: var(--color-charcoal, #212121);
    --btn-color: #fff;
    --focus-btn-color: #fff;
    --inner-background: #fff;
    --inner-opacity: 0;
    --focus-inner-opacity: 0.15;

    &[data-outline="true"] {
      --btn-background: rgba(255, 252, 243, 0.7);
      --btn-color: var(--color-charcoal-50, rgba(33, 33, 33, 0.5));
      --focus-btn-color: var(--color-charcoal-50, rgba(33, 33, 33, 0.5));
      --inner-background: var(--color-charcoal, #212121);
      --focus-inner-opacity: 0.05;
      border: 1px solid var(--color-charcoal-20, rgba(33, 33, 33, 0.2));
      backdrop-filter: blur(12px);
    }
  }

  &[data-outline="true"][data-theme="sunrise"] {
    --inner-background: #fff;
    --inner-opacity: 1;
    --border-size: 2px;
  }

  &[data-outline="true"][data-theme="charcoal"] {
    --inner-background: #fff;
    --inner-opacity: 1;
    --border-size: 2px;
    --btn-color: var(--greys-charcoal, #212121);
  }
  // #endregion

  &[data-size="medium"] {
    --btn-height: 2.625rem;
    --btn-padding-left: 1rem;
    --btn-padding-right: 0.75rem;

    &[data-hide-arrow="true"] {
      --btn-padding-right: 1rem;
    }
  }

  &[data-size="compact"] {
    --btn-height: 2.1rem;
    --btn-padding-left: 0.75rem;
    --btn-padding-right: 0.75rem;

    &[data-hide-arrow="true"] {
      --btn-padding-right: 0.75rem;
    }
  }

  &[data-size="small"] {
    --btn-height: 1.83125rem;
    --btn-padding-left: 1rem;
    --btn-padding-right: 0.75rem;

    &[data-hide-arrow="true"] {
      --btn-padding-right: 1rem;
    }
  }

  &[data-disabled="true"] {
    opacity: 0.7;
    filter: grayscale(1);
    cursor: not-allowed;
  }

  &[data-round="true"] {
    --border-radius: 100rem;
    --btn-padding: 0rem;
    --btn-gap: 0;
    width: var(--btn-height);
    height: var(--btn-height);
  }

  &[data-full-width="true"] {
    width: 100%;
  }

  // #region Hover
  @media (pointer: fine) {
    --focus-btn-color: #fff;

    &:hover {
      &[data-theme="card"] {
        --btn-color: var(--btn-color);
      }

      &[data-theme="charcoal"] {
        --btn-color: var(--focus-btn-color);
      }

      &[data-theme="charcoal"][data-outline="true"],
      &[data-theme="sunrise"],
      &[data-theme="transparent"],
      &[data-theme="glass"],
      &[data-theme="simple"] {
        --btn-color: var(--greys-charcoal);
      }

      &:not(
        [data-disabled="true"],
        [data-theme="rxdiet"],
        [data-theme="glass"],
        [data-theme="subtle"],
        [data-theme="simple"]
      ) {
        box-shadow:
          0px 0px 0px 0px rgba(0, 0, 0, 0.2),
          -3px 3px 9px 0px rgba(0, 0, 0, 0.2),
          -13px 11px 17px 0px rgba(0, 0, 0, 0.17),
          -28px 24px 22px 0px rgba(0, 0, 0, 0.1),
          -50px 43px 26px 0px rgba(0, 0, 0, 0.03),
          -79px 67px 29px 0px rgba(0, 0, 0, 0);
      }

      --btn-color: var(--focus-btn-color);
    }
  }
  // #endregion
`;

const ButtonInner = styled.div`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  gap: var(--btn-gap);
  padding: 0 var(--btn-padding-right) 0 var(--btn-padding-left);

  button[data-round="true"] & {
    padding: 0;
  }

  &::before {
    transition:
      opacity 0.2s ease,
      background-color 0.2s ease;
    background: var(--inner-background, transparent);
    content: "";
    display: block;
    position: absolute;
    inset: var(--border-size);
    border-radius: calc(7.5rem - var(--border-size));
    opacity: var(--inner-opacity, 1);

    @media (pointer: fine) {
      button:not([data-disabled="true"]):hover & {
        opacity: var(--focus-inner-opacity, 1);
      }
    }
  }
`;

const ButtonChildren = styled.div`
  text-align: center;
  font-feature-settings:
    "clig" off,
    "liga" off;
  position: relative;

  font-size: 1rem;
  font-style: normal;
  font-weight: var(--btn-font-weight, 500);
  line-height: 1rem;
  letter-spacing: -0.02rem;
  text-transform: capitalize;

  button[data-action="true"] & {
    font-size: 0.875rem;
    font-style: normal;
    font-weight: var(--btn-font-weight, 400);
    line-height: 140%;
    letter-spacing: -0.0175rem;
    padding-left: 0rem;
  }

  button[data-size="small"] & {
    font-size: 0.875rem;
    font-style: normal;
    font-weight: var(--btn-font-weight, 400);
    line-height: 140%;
    letter-spacing: 0.02em;
  }
`;

const ButtonIcon = styled.div`
  display: block;
  position: relative;

  svg {
    display: block;
  }
`;

const CustomIcon = styled.div`
  display: block;
  position: relative;

  svg {
    display: block;
    max-width: 1.5em;
    height: auto;
    margin-right: 0.6em;
  }

  button[data-icon-only="true"] & svg {
    margin-right: 0;
  }
`;

const ButtonGlassFrame = styled(GlassFrame)`
  --frame-padding: 0;
  --frame-border-radius: var(--border-radius);
  height: 100%;
`;

const ArrowRight: FC = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="19"
      height="18"
      viewBox="0 0 19 18"
      fill="none"
    >
      <path
        d="M7.25 4.5L11.75 9.00049L7.25 13.5"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    </svg>
  );
};

function Button(
  props: {
    /** Whether to grow the button to fill the width of its container. */
    grow?: boolean;
    children?: React.ReactNode;
    outline?: boolean | "initial";
    disabled?: boolean;
    theme?:
      | "danger"
      | "sunrise"
      | "charcoal"
      | "transparent"
      | "card"
      | "blur-charcoal"
      | "rxdiet"
      | "dusk"
      | "glass"
      | "simple"
      | "subtle";
    size?: "small" | "compact" | "medium" | "large";
    fullWidth?: boolean;
    active?: boolean;
    round?: boolean;
    center?: boolean;
    hideArrow?: boolean;
    icon?: React.ReactNode;
    isActionButton?: boolean;
    ref?: React.Ref<HTMLButtonElement | null>;
  } & ButtonProps
) {
  const elRef = React.useRef<HTMLButtonElement>(null);
  const {
    theme = "sunrise",
    outline = false,
    size = "large",
    active = false,
    round = false,
    icon,
    hideArrow,
    children,
    center,
    fullWidth,
    grow,
    isActionButton,
    ...buttonProps
  } = props;
  const isAction =
    typeof isActionButton === "boolean" ? isActionButton : Boolean(icon);
  const compHideArrow = Boolean(hideArrow) || isAction;

  React.useImperativeHandle<typeof elRef.current, typeof elRef.current>(
    props.ref,
    () => elRef.current,
    [elRef]
  );

  const handlePress = (e: PressEvent) => {
    const isSubmit = buttonProps.type === "submit";
    const form = elRef.current?.form;
    if (isSubmit && form) {
      form.requestSubmit(elRef.current);
    }
    buttonProps.onPress?.(e);
  };

  const handleClick = (e: MouseEvent) => {
    // Prevent the default behavior of the button, rely on the onPress event instead
    e.preventDefault();
  };

  useEffect(() => {
    elRef.current?.addEventListener("click", handleClick);
    return () => {
      elRef.current?.removeEventListener("click", handleClick);
    };
  }, [elRef.current]);

  const innerContent = (
    <ButtonInner>
      {icon && <CustomIcon>{icon}</CustomIcon>}
      {children && (
        <ButtonChildren data-testid="button-children">
          {children}
        </ButtonChildren>
      )}
      {!hideArrow && (
        <ButtonIcon>
          <ArrowRight />
        </ButtonIcon>
      )}
    </ButtonInner>
  );

  return (
    <ButtonStyled
      data-round={round}
      data-outline={outline}
      data-theme={theme}
      data-size={size}
      data-hide-arrow={compHideArrow}
      data-action={isAction}
      data-center={center}
      data-active={active}
      data-grow={grow}
      data-full-width={fullWidth}
      data-icon-only={Boolean(icon && !children)}
      {...buttonProps}
      onPress={handlePress}
      ref={elRef}
    >
      {theme === "glass" ? (
        <ButtonGlassFrame>{innerContent}</ButtonGlassFrame>
      ) : (
        innerContent
      )}
    </ButtonStyled>
  );
}

export default Button;
